import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../store";
import { fetchChatbots } from "../../features/ai_chatbots/aiChatbotsSlice";
import { AIChatbot } from "../../features/ai_chatbots/aiChatbotsService";
import { Card, Button, Spinner, Alert, Row, Col } from "react-bootstrap";
import { Icon } from "../../components/common/Icon";
import { FaRobot, FaEye, FaCode, FaEdit, FaTrash, FaComments } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import AIChatbotDetailModal from "../ai_chatbots/components/AIChatbotDetailModal";
import AIChatbotWidgetModal from "../ai_chatbots/components/AIChatbotWidgetModal";
import AIChatbotTestModal from "../ai_chatbots/components/AIChatbotTestModal";
import AIChatbotSessions from "../ai_chatbots/components/AIChatbotSessions";
import companiesService, { Company } from "../../features/companies/companiesService";
import usersService from "../../features/users/usersService";
import AIChatbotFormModal from "../ai_chatbots/components/AIChatbotFormModal";
import ConfirmationModal from "../../components/common/ConfirmationModal";
import { deleteChatbot } from "../../features/ai_chatbots/aiChatbotsSlice";
import { isAdmin } from "../../utils/permissions";
import { setSelectedClient } from "../../features/settings/settingsSlice";

const AIToolsChatbotsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { chatbots, isLoading, error, page, limit } = useSelector((s: RootState) => s.aiChatbots);
  const { selectedClientId } = useSelector((s: RootState) => s.settings);
  const { user } = useSelector((s: RootState) => s.auth);
  const { t } = useTranslation();

  const [search, setSearch] = useState("");
  const [myCompanyId, setMyCompanyId] = useState<number | undefined>(undefined);
  const [selectedChatbotId, setSelectedChatbotId] = useState<number | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showWidgetModal, setShowWidgetModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createCompanyId, setCreateCompanyId] = useState<number | undefined>(undefined);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSessionsModal, setShowSessionsModal] = useState(false);
  const [singleCompanies, setSingleCompanies] = useState<Company[]>([]);
  const [selectedSingleCompanyId, setSelectedSingleCompanyId] = useState<number | undefined>(undefined);

  // Load single company id for company owner if not selected; for company_specialist load from my-companies
  useEffect(() => {
    const roles = user?.roles || [];
    const isOwner = roles.includes("company_owner");
    const isSpecialist = roles.includes("company_specialist");
    (async () => {
      try {
        if (!selectedClientId && myCompanyId === undefined) {
          if (isOwner) {
            const c = await companiesService.getMyCompany();
            setMyCompanyId(c?.id);
          } else if (isSpecialist) {
            const list = await usersService.getMyCompanies();
            const single = Array.isArray(list) ? list.find((c: any) => c.company_type === "single") : undefined;
            if (single?.id) setMyCompanyId(single.id);
          }
        }
      } catch {
        // ignore
      }
    })();
  }, [user, selectedClientId, myCompanyId]);

  // Load selectable single-type companies for Admin/Super Admin
  useEffect(() => {
    const loadSingles = async () => {
      if (!isAdmin(user)) return;
      try {
        const { companies } = await companiesService.getCompanies({ company_type: 'single', limit: 200 });
        setSingleCompanies(companies || []);
        if (!selectedSingleCompanyId && companies && companies.length > 0) {
          setSelectedSingleCompanyId(companies[0].id);
        }
      } catch {
        setSingleCompanies([]);
      }
    };
    loadSingles();
  }, [user, selectedSingleCompanyId]);

  // Force single company filter (company_type single is implied by selectedClientId in this area)
  const roles = user?.roles || [];
  const isOwner = roles.includes("company_owner");
  const effectiveFilters = useMemo(() => ({
    company_id: selectedClientId || myCompanyId || (isAdmin(user) ? selectedSingleCompanyId : undefined),
    search: search || undefined,
    module: 'ai_tools'
  }), [selectedClientId, myCompanyId, selectedSingleCompanyId, search, user]);
  const waitingForCompany = isOwner && !selectedClientId && !myCompanyId;

  useEffect(() => {
    if (waitingForCompany) return; // defer until company is known for company roles
    const params: any = { ...effectiveFilters, page, limit };
    dispatch(fetchChatbots(params));
  }, [dispatch, page, limit, effectiveFilters, waitingForCompany]);

  const isCompanyOwner = isOwner;

  const renderGrid = (items: AIChatbot[]) => (
    <Row className="chatbots-grid">
      {items.map((chatbot) => (
        <Col key={chatbot.id} lg={4} md={6} className="mb-4">
          <Card className="chatbot-card">
            <Card.Header className="chatbot-header">
              <div className="chatbot-title">
                <div className="chatbot-icon">
                  <Icon icon={FaRobot} />
                </div>
                <div className="chatbot-info">
                  <h5 className="chatbot-name">{chatbot.name}</h5>
                  <span className={`status-badge ${chatbot.is_active ? "active" : "inactive"}`}>
                    <span className="status-indicator"></span>
                    {chatbot.is_active ? t("active") : t("inactive")}
                  </span>
                </div>
              </div>
            </Card.Header>
            <Card.Body className="chatbot-body">
              <p className="chatbot-description">{chatbot.description || t("no_description")}</p>
              <div className="chatbot-details">
                <div className="detail-item">
                  <span className="detail-label">{t("model")}:</span>
                  <span className="detail-value">{chatbot.model}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">{t("created")}:</span>
                  <span className="detail-value">{new Date(chatbot.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            </Card.Body>
            <Card.Footer className="chatbot-footer">
              <div className="action-buttons">
                <Button variant="outline-info" size="sm" className="action-btn" onClick={() => { setSelectedChatbotId(chatbot.id); setShowDetailModal(true); }}>
                  <Icon icon={FaEye} className="me-1" />
                  {t("details")}
                </Button>
                <Button variant="outline-success" size="sm" className="action-btn" onClick={() => { setSelectedChatbotId(chatbot.id); setShowWidgetModal(true); }}>
                  <Icon icon={FaCode} className="me-1" />
                  {t("widget")}
                </Button>
                <Button variant="outline-secondary" size="sm" className="action-btn" onClick={() => { setSelectedChatbotId(chatbot.id); setShowTestModal(true); }}>
                  <Icon icon={FaRobot} className="me-1" />
                  {t("test")}
                </Button>
                <Button variant="outline-dark" size="sm" className="action-btn" onClick={() => { setSelectedChatbotId(chatbot.id); setShowSessionsModal(true); }}>
                  <Icon icon={FaComments} className="me-1" />
                  {t("chatbot_sessions")}
                </Button>
                {isCompanyOwner && (
                  <Button variant="outline-primary" size="sm" className="action-btn" onClick={() => { setSelectedChatbotId(chatbot.id); setShowEditModal(true); }}>
                    <Icon icon={FaEdit} className="me-1" />
                    {t("edit")}
                  </Button>
                )}
                {isCompanyOwner && (
                  <Button variant="outline-danger" size="sm" className="action-btn delete-btn" onClick={() => { setSelectedChatbotId(chatbot.id); setShowDeleteModal(true); }}>
                    <Icon icon={FaTrash} className="me-1" />
                    {t("delete")}
                  </Button>
                )}
              </div>
            </Card.Footer>
          </Card>
        </Col>
      ))}
    </Row>
  );

  const handleOpenCreate = async () => {
    let targetId = effectiveFilters.company_id || myCompanyId;
    if (!targetId) {
      try {
        const c = await companiesService.getMyCompany();
        if (c?.id) {
          targetId = c.id;
          setMyCompanyId(c.id);
        }
      } catch {}
    }
    setCreateCompanyId(targetId);
    setShowCreateModal(true);
  };

  return (
    <div className="ai-tools-chatbots">
      <div className="page-header">
        <div className="header-left">
          <div className="header-icon">
            <Icon icon={FaRobot} />
          </div>
          <div className="header-text">
            <h1>{t("ai_chatbots")}</h1>
            <p>{t("manage_your_ai_powered_chatbot_assistants")}</p>
          </div>
        </div>
        <div className="header-right" style={{ width: "100%", maxWidth: 600 }}>
          {isAdmin(user) && (
            <div style={{ minWidth: 220, marginRight: 12 }}>
              <select
                className="form-select"
                value={selectedSingleCompanyId || ''}
                onChange={(e) => {
                  const val = e.target.value ? Number(e.target.value) : undefined;
                  setSelectedSingleCompanyId(val);
                  // propagate to global settings so other tools open for this company
                  (dispatch as any)(setSelectedClient(val));
                }}
              >
                <option value="">{t("select_company") || "Select company"}</option>
                {singleCompanies.map((c) => (
                  <option key={c.id} value={c.id}>{c.company_name}</option>
                ))}
              </select>
            </div>
          )}
          <div className="search-box" style={{ width: "100%" }}>
            <i className="bi bi-search" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("search_placeholder") as string}
            />
          </div>
        </div>
      </div>

      <div className="content">
        {(isLoading || waitingForCompany) && (
          <div className="loading-container">
            <Spinner animation="border" className="loading-spinner" />
          </div>
        )}
        {!waitingForCompany && error && (
          <Alert className="error-alert">{error}</Alert>
        )}
        {!isLoading && !waitingForCompany && !error && chatbots.length > 0 && (
          <>
            {isCompanyOwner && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                <Button className="create-btn" onClick={handleOpenCreate}>{t("add_new_ai_chatbot")}</Button>
              </div>
            )}
            {renderGrid(chatbots)}
          </>
        )}
        {!isLoading && !waitingForCompany && !error && chatbots.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">
              <Icon icon={FaRobot} />
            </div>
            <h3 className="empty-title">{t("not_found")}</h3>
            <p className="empty-description">{t("no_chatbots_found")}</p>
            {isCompanyOwner && (
              <Button className="create-btn" onClick={handleOpenCreate}>
                {t("add_new_ai_chatbot")}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Modals re-used from marketing dashboard components */}
      {showDetailModal && (
        <AIChatbotDetailModal
          show={showDetailModal}
          onHide={() => setShowDetailModal(false)}
          chatbotId={selectedChatbotId}
        />
      )}
      {showWidgetModal && (
        <AIChatbotWidgetModal
          show={showWidgetModal}
          onHide={() => setShowWidgetModal(false)}
          chatbotId={selectedChatbotId}
        />
      )}
      {showTestModal && (
        <AIChatbotTestModal
          show={showTestModal}
          onHide={() => setShowTestModal(false)}
          chatbot={selectedChatbotId ? chatbots.find((c) => c.id === selectedChatbotId) || null : null}
        />
      )}

      {/* Sessions Modal */}
      {showSessionsModal && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {t("chatbot_sessions")} – {selectedChatbotId ? (chatbots.find((c) => c.id === selectedChatbotId)?.name || '') : ''}
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowSessionsModal(false)} />
              </div>
              <div className="modal-body">
                {/* Reuse sessions component filtered for this chatbot */}
                <AIChatbotSessions defaultChatbotId={selectedChatbotId ?? undefined} hideChatbotSelect />
              </div>
              <div className="modal-footer">
                <Button variant="secondary" onClick={() => setShowSessionsModal(false)}>{t("close")}</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCreateModal && (
        <AIChatbotFormModal
          show={showCreateModal}
          onHide={() => setShowCreateModal(false)}
          defaultCompanyId={createCompanyId}
          defaultModule="ai_tools"
          // Pre-set module for AI Tools when creating here
          initialActiveTab={"general"}
          onSuccess={() => {
            setShowCreateModal(false);
            dispatch(fetchChatbots({ ...effectiveFilters, page, limit }));
          }}
        />
      )}

      {showEditModal && (
        <AIChatbotFormModal
          show={showEditModal}
          onHide={() => setShowEditModal(false)}
          chatbot={selectedChatbotId ? chatbots.find((c) => c.id === selectedChatbotId) : undefined}
          onSuccess={() => {
            setShowEditModal(false);
            dispatch(fetchChatbots({ ...effectiveFilters, page, limit }));
          }}
        />
      )}

      <ConfirmationModal
        show={showDeleteModal}
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={() => {
          if (selectedChatbotId) {
            dispatch(deleteChatbot(selectedChatbotId) as any)
              .unwrap()
              .then(() => {
                setShowDeleteModal(false);
                dispatch(fetchChatbots({ ...effectiveFilters, page, limit }));
              })
              .catch(() => setShowDeleteModal(false));
          }
        }}
        title={t("delete_chatbot")}
        message={t("are_you_sure_delete_chatbot")}
        confirmText={t("delete")}
      />

      <style>{`
.ai-tools-chatbots { padding: 24px; }
.page-header { display:flex; justify-content:space-between; gap:16px; padding:20px 24px; background:var(--bg-primary); border:1px solid var(--border-color); border-radius:16px; }
.header-left { display:flex; align-items:center; gap:14px; }
.header-icon { width:46px; height:46px; border-radius:12px; background: linear-gradient(135deg, var(--accent-primary), var(--accent-secondary)); color:#fff; display:flex; align-items:center; justify-content:center; font-size:20px; }
.header-text h1 { margin:0; font-size:22px; font-weight:700; color:var(--text-primary); }
.header-text p { margin:2px 0 0; color:var(--text-muted); font-size:14px; font-weight:500; }
.search-box { display:flex; align-items:center; gap:8px; background: var(--bg-tertiary); border:1px solid var(--border-color); border-radius:12px; padding:10px 12px; }
.search-box input { border:none; outline:none; background:transparent; width:100%; color:var(--text-primary); font-size:14px; }

/* Grid and card styles (aligned with Marketing Dashboard look) */
.chatbots-grid { margin-top: 16px; }
.chatbot-card { background: var(--bg-primary) !important; border: 1px solid rgba(148,163,184,.6) !important; border-radius: 16px !important; box-shadow: 0 2px 8px rgba(15,23,42,.06) !important; transition: all .2s ease !important; overflow: hidden; height: 100%; }
.chatbot-card:hover { transform: translateY(-2px); box-shadow: 0 8px 25px rgba(0,0,0,.12) !important; }
.chatbot-header { background: linear-gradient(180deg, rgba(241,245,249,.8), rgba(241,245,249,0)) !important; border-bottom: 1px solid var(--border-color) !important; padding: 16px 20px !important; }
.chatbot-title { display:flex; align-items:center; gap:12px; }
.chatbot-icon { width: 36px; height: 36px; background: linear-gradient(135deg, var(--accent-primary), var(--accent-secondary)); border-radius: 10px; display:flex; align-items:center; justify-content:center; color:#fff; font-size:16px; }
.chatbot-info { flex:1; min-width:0; }
.chatbot-name { font-size: 18px; font-weight:600; color: var(--text-primary); margin:0; }
.status-badge { display:inline-flex; align-items:center; gap:6px; padding:4px 10px; border-radius:20px; font-size:12px; font-weight:600; text-transform:uppercase; letter-spacing:.5px; }
.status-badge.active { background: rgba(16,185,129,.1); color:#10b981; border:1px solid rgba(16,185,129,.2); }
.status-badge.inactive { background: rgba(100,116,139,.1); color:#64748b; border:1px solid rgba(100,116,139,.2); }
.status-indicator { width:6px; height:6px; border-radius:50%; background: currentColor; }
.chatbot-body { padding:20px !important; background: var(--bg-primary) !important; display:flex; flex-direction:column; gap:12px; }
.chatbot-description { color: var(--text-secondary); font-size:14px; margin:0; }
.chatbot-details { display:flex; flex-direction:column; gap:8px; }
.detail-item { display:flex; justify-content:space-between; align-items:center; font-size:14px; }
.detail-label { color: var(--text-muted); font-weight:500; }
.detail-value { color: var(--text-primary); font-weight:600; }
.chatbot-footer { background: var(--bg-tertiary) !important; border-top: 1px solid var(--border-color) !important; padding: 16px 20px !important; }
.action-buttons { display:flex; flex-wrap:wrap; gap:8px; }
.action-btn { padding:6px 12px !important; border-radius:8px !important; font-size:12px !important; font-weight:600 !important; display:flex; align-items:center; gap:4px; border:1px solid currentColor !important; background: transparent !important; }
      `}</style>
    </div>
  );
};

export default AIToolsChatbotsPage;




